import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { GranulationTypeId, Theme, TimeUnit } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';

import didUpdate from 'webapp/modifiers/did-update';
import toggleScrolledClass from 'webapp/modifiers/toggle-scrolled-class';
import { MetricInsightsChartState } from 'webapp/components/metric-details/explore/data';
import MetricDetailsExploreDataComponent from 'webapp/components/metric-details/explore/data/index';
import MetricDetailsExploreRelatedMetricsComponent from 'webapp/components/metric-details/explore/related-metrics/index';
import MetricDetailsExploreToolbarComponent from 'webapp/components/metric-details/explore/toolbar/index';
import MetricDetailsGoalsComponent from 'webapp/components/metric-details/goals';
import MetricDetailsRelatedForecastsComponent from 'webapp/components/metric-details/related-forecasts';

import type { Metric } from 'webapp/models-custom';
import DateRange from 'webapp/models-custom/date-range';
import type { MetricLimitationsConstructOptions } from 'webapp/models-custom/metric-limitations';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import MetricSetup from 'webapp/models-custom/metric-setup';

import type { ExploreTabState, SharedMetricDetailsState } from 'webapp/services/metric-details';
import type { TrackingOriginForMetricEdit } from 'webapp/services/model-manager/metrics';

interface Args {
	sharedState: SharedMetricDetailsState;
	initialTabState: ExploreTabState;
	onTabStateChange: (state: ExploreTabState) => void;
	onSharedStateChange: (data: SharedMetricDetailsState) => void;
	onShare?: () => void;
	onClose?: () => void;
}

interface MetricDetailsExploreComponentInterface {
	Args: Args;
	Blocks: { header: [] };
}

export default class MetricDetailsExploreComponent extends Component<MetricDetailsExploreComponentInterface> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('billing') declare billingService: Service.Billing;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('analytics') declare analyticsService: Service.Analytics;

	metricSetup: MetricSetup;
	@tracked limitations: MetricLimitations;
	@tracked chartState: MetricInsightsChartState;
	origin: TrackingOriginForMetricEdit = 'metrics_detail';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const metricSetup = new MetricSetup();
		metricSetup.setFromMetricSetupData(this.args.sharedState.metricSetup);

		metricSetup.onChange = () => {
			this.notifyMetricSetupChange();
		};

		this.metricSetup = metricSetup;

		const initialFormula = this.args.initialTabState.dateRange;
		const dateRange = initialFormula
			? DateRange.createFromFormula(initialFormula)
			: DateRange.createLastX(30, TimeUnit.DAY, GranulationTypeId.DAILY);

		const limitationsObj: MetricLimitationsConstructOptions = {
			dateRange,
		};

		this.redirectToDefaultIfNoMetric();

		if (metricSetup.metricData) {
			limitationsObj.metrics = [metricSetup.metricData];
		}

		this.limitations = new MetricLimitations(limitationsObj);
		this.checkLimitations();

		this.chartState = new MetricInsightsChartState();

		void this.validateAndCorrectDateRange();
	}

	@tracked loadingLimitationsForKey = '';
	checkLimitations() {
		const promise = this.limitations.limitationsLoaded;
		if (promise) {
			this.loadingLimitationsForKey = JSON.stringify(this.metricSetup.metricData);
			void promise
				.then(() => {
					const metricLimitationRange = this.limitations.limitationsForSelectedRange,
						fallbackDateRange = this.getFallbackDateRange(),
						isRangeFallback = this.dateRange?.isRangeEqual(fallbackDateRange.formula); // do not validate fallbackDateRange

					if (metricLimitationRange?.disabled && !isRangeFallback) {
						/*
						Do not use
							- updateDateRangeFromOnlyRangeFormula and
							- selectOptimalValidGranulation
						cause isRangeFallback might return false and endlessly update limitations DR
						*/

						next(() => {
							// Update tab state instead of this.limitations.updateDateRangeFromFormula(fallbackDateRange.formula) directly
							this.args.onTabStateChange(
								this.buildExploreState({ dateRange: fallbackDateRange.formula }),
							);
						});
					}
				})
				.finally(() => {
					// Move runloop, so this.dateRange and this.loadingLimitationsForKey change in the same runloop, and we don't make premature requests
					next(() => {
						// In next as this.changeDateRange above
						next(() => {
							// In next as updateDateRange() updates limtiations
							if (
								this.loadingLimitationsForKey ===
								JSON.stringify(this.metricSetup.metricData)
							) {
								this.loadingLimitationsForKey = '';
							}
						});
					});
				});
		}
	}

	get theme() {
		return this.navigationService.theme.id || Theme.LIGHT;
	}

	get dateRange() {
		return this.limitations.dateRange!;
	}

	private notifyMetricSetupChange(): void {
		const metricSetupData = this.metricSetup.metricSetupData;
		if (metricSetupData) {
			this.args.onSharedStateChange({ metricSetup: metricSetupData });
		}
	}

	private buildExploreState(overrides: Partial<ExploreTabState>): ExploreTabState {
		return {
			...this.args.initialTabState,
			...overrides,
		};
	}

	get forceLoadingState() {
		return !!this.loadingLimitationsForKey;
	}

	trackMetricChanged(): void {
		if (this.metricSetup.metric) {
			this.metricDetailsService.trackMetricChanged(this.metricSetup.metric);
		}
	}

	@action
	onAnomalyStateChanged(changes: Partial<ExploreTabState>): void {
		this.args.onTabStateChange(this.buildExploreState(changes));
	}

	private redirectToDefaultIfNoMetric(): void {
		if (!this.metricSetup.key) {
			// preselect something for the user
			// metric-details validated input - empty is valid in case invalid input was provided
			next(() => {
				this.metricDetailsService.selectDefaultMetric();
			});
		}
	}

	private async validateAndCorrectDateRange() {
		const correctedDateRange = await this.metricDetailsService.validateDateRange(
			this.args.initialTabState.dateRange,
			this.metricSetup.metricData || undefined,
		);

		if (correctedDateRange) {
			this.args.onTabStateChange(
				this.buildExploreState({ dateRange: correctedDateRange.formula }),
			);
			this.limitations.updateDateRangeFromFormula(correctedDateRange.formula);
		}
	}

	getFallbackDateRange() {
		const activePlan = this.billingService.activePlan,
			historicalDataRetention = activePlan?.features.historicalDataRetention as number | null,
			canFetchLastYear =
				!!activePlan && (historicalDataRetention === null || historicalDataRetention >= 12);

		const dateRange = DateRange.createLastX(
			canFetchLastYear ? 1 : 6,
			canFetchLastYear ? TimeUnit.YEAR : TimeUnit.MONTH,
			GranulationTypeId.MONTHLY,
		);

		return dateRange;
	}

	// fires on any @initialTabState change — both external (URL, tab switch) and callback from the data component's syncTabStateFromBlock
	@action
	onOutsideDateRangeChange() {
		const dateRangeFormula = this.args.initialTabState.dateRange;
		if (dateRangeFormula) {
			this.limitations.updateDateRangeFromFormula(dateRangeFormula);
		}
	}

	@action
	onOutsideMetricSetupChange(): void {
		const newMetricSetup = new MetricSetup();
		newMetricSetup.setFromMetricSetupData(this.args.sharedState.metricSetup);

		if (!this.metricSetup.isMatchingMetricSetup(newMetricSetup)) {
			this.metricSetup.setFromMetricSetupData(this.args.sharedState.metricSetup);

			this.limitations.changeMetrics([this.metricSetup.metricData!]);
			this.checkLimitations();

			debounce(this, this.trackMetricChanged, 300);
		}
	}

	@action
	onMetricEdit(metric: Metric): void {
		if (metric) {
			this.metricSetup.setFromMetricData(metric.metricData);
		}

		this.eventManagerService.trigger(AppEvent.METRIC_INSIGHTS_METRIC_EDITED);
	}
	<template>
		<div
			class="metric-details__content"
			{{toggleScrolledClass}}
			{{didUpdate this.onOutsideDateRangeChange @initialTabState}}
			{{didUpdate this.onOutsideMetricSetupChange @sharedState}}
		>
			<div class="metric-details__header-row">
				{{yield to="header"}}
				{{#if this.metricSetup.metric}}
					<MetricDetailsExploreToolbarComponent
						@metricSetup={{this.metricSetup}}
						@dateRange={{this.dateRange}}
						@origin={{this.origin}}
						@onMetricEdit={{this.onMetricEdit}}
						@onShare={{@onShare}}
						@onClose={{@onClose}}
					/>
				{{/if}}
			</div>

			{{#if this.metricSetup.metric}}
				<MetricDetailsExploreDataComponent
					@metricSetup={{this.metricSetup}}
					@dateRange={{this.dateRange}}
					@forceLoadingState={{this.forceLoadingState}}
					@chartState={{this.chartState}}
					@origin={{this.origin}}
					@initialTabState={{@initialTabState}}
					@onTabStateChange={{@onTabStateChange}}
					@onAnomalyStateChanged={{this.onAnomalyStateChanged}}
				/>

				<MetricDetailsGoalsComponent
					@metricSetup={{this.metricSetup}}
					@origin={{this.origin}}
				/>

				{{! it requests the saved forecasts as soon as it renders }}
				{{#unless this.metricDetailsService.isMobileMode}}
					<MetricDetailsRelatedForecastsComponent @metricSetup={{this.metricSetup}} />
				{{/unless}}

				<MetricDetailsExploreRelatedMetricsComponent
					@metricSetup={{this.metricSetup}}
					@chartState={{this.chartState}}
					@dateRange={{this.dateRange}}
					@forceLoadingState={{this.forceLoadingState}}
					@origin={{this.origin}}
				/>
			{{/if}}
		</div>
	</template>
}
